import {
  AdvertiserWebService,
  RestfulAdvertiserWebService,
  AdvertisersWithPagination,
  MerchantsWithPagination
} from 'ws/AdvertiserWebService';
import { Advertiser } from './Advertiser';
import { Pageable } from 'ws/Pageable';
import { CompanyMember } from 'core/companyMember/CompanyMember';
import { CompanyRole } from 'core/companyMember/CompanyRole';
import {
  BeaconConnection,
  BeaconConnectionFormData,
  LineBotInfo
} from 'core/advertiser/BeaconConnection';
import { generateI18nOfSelectOptions } from 'utils/I18nUtils';

export interface AdvertiserManager {
  getAdvertiser(advertiserId): Promise<Advertiser>;
  getAdvertiserCategory(advertiserId): Promise<string>;
  createAdvertiser(advertiser: Partial<Advertiser>): Promise<number>;
  updateAdvertiser(advertiser: Partial<Advertiser>): Promise<number>;
  getAdvertisers(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<AdvertisersWithPagination>;
  getAdvertiserOptions(agencyId?: number | null): Promise<Array<SelectOptions>>;
  getAdvertiserMembers(
    advertiserId: number | string
  ): Promise<Array<CompanyMember>>;
  deleteAdvertiserMembers(
    advertiserId: number,
    accountId: number
  ): Promise<void>;
  getAdvertiserMember(
    advertiserId: number,
    accountId: number
  ): Promise<CompanyMember>;
  createAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void>;
  updateAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void>;
  updateAdvertiserShareSegment(
    advertiserId: number,
    segmentSourceIds: number[]
  ): Promise<void>;
  createBeaconConnection(
    advertiserId: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void>;
  updateBeaconConnection(
    advertiserId: number,
    id: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void>;
  getBeaconConnections(advertiserId: number): Promise<BeaconConnection[]>;
  getBeaconConnectionOptions(advertiserId: number): Promise<SelectOptions[]>;
  getLineBeaconCoupons(
    advertiserId: number,
    beaconConnectionId: string
  ): Promise<SelectOptions[]>;
  getLineBotInfo(
    advertiserId: number,
    channelAccessToken: string
  ): Promise<LineBotInfo>;
  getMerchants(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<MerchantsWithPagination>;
}

export class DefaultAdvertiserManager implements AdvertiserManager {
  webService: AdvertiserWebService;

  constructor(
    webService: AdvertiserWebService = new RestfulAdvertiserWebService()
  ) {
    this.webService = webService;
  }

  async getAdvertiser(advertiserId): Promise<Advertiser> {
    return this.webService.getAdvertiser(advertiserId);
  }

  async getAdvertiserCategory(advertiserId): Promise<string> {
    return this.webService.getAdvertiserCategory(advertiserId);
  }

  async createAdvertiser(advertiser: Partial<Advertiser>): Promise<number> {
    return this.webService.createAdvertiser(advertiser);
  }

  async updateAdvertiser(advertiser: Partial<Advertiser>): Promise<number> {
    return this.webService.updateAdvertiser(advertiser);
  }

  async getAdvertisers(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<AdvertisersWithPagination> {
    return this.webService.getAdvertisers(agencyId, pageable, search);
  }

  async getAdvertiserOptions(agencyId?: number): Promise<Array<SelectOptions>> {
    const options = await this.webService.getAdvertiserOptions(agencyId);
    options.forEach((option) =>
      generateI18nOfSelectOptions(option, 'advertisers')
    );
    return options;
  }

  async getAdvertiserMembers(
    advertiserId: number | string
  ): Promise<Array<CompanyMember>> {
    return this.webService.getAdvertiserMembers(advertiserId);
  }

  async deleteAdvertiserMembers(
    advertiserId: number,
    accountId: number
  ): Promise<void> {
    return this.webService.deleteAdvertiserMembers(advertiserId, accountId);
  }

  async getAdvertiserMember(
    advertiserId: number,
    accountId: number
  ): Promise<CompanyMember> {
    return this.webService.getAdvertiserMember(advertiserId, accountId);
  }

  async createAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void> {
    return this.webService.createAdvertiserMember(advertiserId, companyRole);
  }

  async updateAdvertiserMember(
    advertiserId: number,
    companyRole: CompanyRole
  ): Promise<void> {
    return this.webService.updateAdvertiserMember(advertiserId, companyRole);
  }

  async updateAdvertiserShareSegment(
    advertiserId: number,
    segmentSourceIds: number[]
  ): Promise<void> {
    return this.webService.updateAdvertiserShareSegment(
      advertiserId,
      segmentSourceIds
    );
  }

  async createBeaconConnection(
    advertiserId: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void> {
    return this.webService.createBeaconConnection(
      advertiserId,
      beaconConnection
    );
  }

  async updateBeaconConnection(
    advertiserId: number,
    id: number,
    beaconConnection: BeaconConnectionFormData
  ): Promise<void> {
    return this.webService.updateBeaconConnection(
      advertiserId,
      id,
      beaconConnection
    );
  }

  async getBeaconConnections(
    advertiserId: number
  ): Promise<BeaconConnection[]> {
    return this.webService.getBeaconConnections(advertiserId);
  }

  async getBeaconConnectionOptions(
    advertiserId: number
  ): Promise<SelectOptions[]> {
    return this.webService.getBeaconConnectionOptions(advertiserId);
  }

  async getLineBeaconCoupons(
    advertiserId: number,
    beaconConnectionId: string
  ): Promise<SelectOptions[]> {
    const coupons = await this.webService.getLineBeaconCoupons(
      advertiserId,
      beaconConnectionId
    );
    coupons.forEach((coupon) =>
      generateI18nOfSelectOptions(coupon, 'lineBeaconCoupons')
    );
    return coupons;
  }

  async getLineBotInfo(
    advertiserId: number,
    channelAccessToken: string
  ): Promise<LineBotInfo> {
    return this.webService.getLineBotInfo(advertiserId, channelAccessToken);
  }

  async getMerchants(
    agencyId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<MerchantsWithPagination> {
    return this.webService.getMerchants(agencyId, pageable, search);
  }
}
